import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, delay, map } from "rxjs/operators";
import { environment } from "src/environments/environment";

/** Fréquence affichée / passée en `billing` sur `/checkout`. */
export type PricingBillingPeriod = "monthly" | "yearly";

/** Réduction sur le total annuel vs 12 × mensuel (0.2 = payez 80 % de 12 mois). */
export const PRICING_YEARLY_DISCOUNT_FRACTION = 0.2;

/** Public pricing card as shown on the marketing site (and expected from GET `/website/pricing-plans`). */
export interface PricingPlan {
    name: string;
    /** Pastille / bandeau (ex. offre mise en avant). Souvent absent sauf plan vedette. */
    badge?: string;
    price: string;
    description: string;
    featured?: boolean;
    items: string[];
    ctaLabel: string;
    /** When set, CTA opens mailto instead of the checkout flow. */
    ctaHref?: string;
    /** Plan code passed to `/checkout?plan=…`. */
    planId?: string;
    /**
     * Montant à encaisser en Ariary (MGA) sur le checkout (mobile money USSD).
     * `0` ou absent = pas de paiement en ligne pour ce plan.
     */
    checkoutAmountMga?: number;
    /**
     * Mensuel de référence (Ariary) pour libellés et total annuel quand `checkoutAmountMga` vaut 0
     * (ex. offre « contact » avec prix public au mois).
     */
    monthlyReferenceMga?: number;
}

/** Montants annuels pour la ligne de détail (affichage avec `currency` dans les templates). */
export interface PricingAmountYearlySubtitle {
    totalYearMga: number;
    savingsMga: number;
}

/** Segments pour affichage du prix (montants en MGA pour le `currency` pipe). */
export interface PricingAmountLabelParts {
    /** Montant principal (MGA), ex. mensuel ou équivalent mensuel en annuel. */
    headlineAmountMga: number;
    /** Ex. `/mois`. */
    suffix: string;
    /** Présent en facturation annuelle : totaux pour la ligne secondaire. */
    yearlySubtitle?: PricingAmountYearlySubtitle;
}

const MOCK_PRICING_PLANS: PricingPlan[] = [
    {
        name: "Essai gratuit",
        price: "Gratuit",
        description:
            "Découvrez Rayalina et testez les fonctionnalités essentielles pour gérer vos ventes et vos clients pendant 1 mois.",
        items: [
            "Gestion des commandes",
            "Création de factures",
            "Suivi des paiements",
            "Gestion des clients",
            "Tableau de bord simple",
            "Usage limité pendant l’essai",
        ],
        ctaLabel: "Essayer",
        planId: "trial",
        checkoutAmountMga: 0,
    },
    {
        name: "Essentiel",
        price: "39 000 Ar/mois",
        description:
            "La formule parfaite pour les petits vendeurs qui veulent organiser leurs ventes sans complexité.",
        items: [
            "Tout l’essai gratuit",
            "Ventes illimitées dans le cadre de l’usage standard",
            "Historique des ventes",
            "Export simple en PDF ou Excel",
            "Tableau de bord quotidien",
        ],
        ctaLabel: "Choisir cette offre",
        planId: "essentiel",
        checkoutAmountMga: 39_000,
    },
    {
        name: "Pro",
        badge: "Recommandé",
        price: "59 000 Ar/mois",
        description:
            "Pour les entreprises qui ont besoin d’une gestion commerciale plus complète et plus structurée.",
        featured: true,
        items: [
            "Tout l’offre Essentiel",
            "Gestion des achats",
            "Gestion des fournisseurs",
            "Gestion des stocks",
            "Gestion des articles et variantes",
            "Alertes de rupture de stock",
            "Jusqu'à 4 utilisateurs",
        ],
        ctaLabel: "Passer à Pro",
        planId: "pro",
        checkoutAmountMga: 59_000,
    },
    {
        name: "Entreprise",
        price: "99 000 Ar/mois",
        description:
            "Une solution pensée pour les structures plus organisées, avec plusieurs utilisateurs et des besoins avancés.",
        items: [
            "Tout l’offre Pro",
            "Gestion des rôles et permissions",
            "Multi entrepôts",
            "Rapports avancés",
            "Support prioritaire",
            "Jusqu'à 10 utilisateurs",
        ],
        ctaLabel: "Nous contacter",
        ctaHref: "mailto:contact@rayalina.com?subject=Rayalina%20-%20Demande%20Entreprise",
        planId: "entreprise",
        checkoutAmountMga: 0,
        monthlyReferenceMga: 99_000,
    },
];

@Injectable({
    providedIn: "root",
})
export class PricingService {
    constructor(private readonly http: HttpClient) {}

    /** Mensuel utilisé pour mensuel / annuel (priorité au montant checkout s’il est > 0). */
    referenceMonthlyMga(plan: PricingPlan): number | null {
        const checkout = plan.checkoutAmountMga ?? 0;
        if (checkout > 0) {
            return checkout;
        }
        const ref = plan.monthlyReferenceMga ?? 0;
        return ref > 0 ? ref : null;
    }

    static yearlyTotalFromMonthly(monthlyMga: number): number {
        return Math.round(monthlyMga * 12 * (1 - PRICING_YEARLY_DISCOUNT_FRACTION));
    }

    /** Économie vs payer 12 mois au tarif mensuel (même base mensuelle). */
    static yearlySavingsVsTwelveMonthlyMga(monthlyMga: number): number {
        const yearly = PricingService.yearlyTotalFromMonthly(monthlyMga);
        return Math.round(monthlyMga * 12 - yearly);
    }

    /** Mensuel moyen à partir du total annuel (affichage comparatif). */
    static equivalentMonthlyFromYearlyTotal(yearlyMga: number): number {
        return Math.round(yearlyMga / 12);
    }

    formatMga(amount: number): string {
        return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(amount);
    }

    /**
     * Montant + suffixe (/mois, /an…) pour mise en forme (ex. suffixe en plus petit).
     * `null` si le prix vient tel quel de `plan.price` (ex. essai gratuit).
     */
    amountLabelPartsForPlan(plan: PricingPlan, billing: PricingBillingPeriod): PricingAmountLabelParts | null {
        const m = this.referenceMonthlyMga(plan);
        if (m == null) {
            return null;
        }
        if (billing === "monthly") {
            return { headlineAmountMga: m, suffix: "/mois" };
        }
        const yearlyTotal = PricingService.yearlyTotalFromMonthly(m);
        const eqMonthly = PricingService.equivalentMonthlyFromYearlyTotal(yearlyTotal);
        const savingsMga = PricingService.yearlySavingsVsTwelveMonthlyMga(m);
        return {
            headlineAmountMga: eqMonthly,
            suffix: "/mois",
            yearlySubtitle: { totalYearMga: yearlyTotal, savingsMga },
        };
    }

    /** Libellé prix principal sur la carte ou le récap checkout. */
    amountLabelForPlan(plan: PricingPlan, billing: PricingBillingPeriod): string {
        const parts = this.amountLabelPartsForPlan(plan, billing);
        if (parts) {
            let s = `${this.formatMga(parts.headlineAmountMga)} Ar${parts.suffix}`;
            if (parts.yearlySubtitle) {
                const y = parts.yearlySubtitle;
                s += ` soit ${this.formatMga(y.totalYearMga)} Ar/an, payés en une fois. Vous économisez ${this.formatMga(y.savingsMga)} Ar`;
            }
            return s;
        }
        return plan.price;
    }

    /**
     * Montant à encaisser (Ariary) selon la période. Les offres `ctaHref` restent sans encaissement en ligne.
     */
    effectiveCheckoutAmountMga(plan: PricingPlan, billing: PricingBillingPeriod): number {
        if (plan.ctaHref) {
            return 0;
        }
        const m = this.referenceMonthlyMga(plan);
        if (m == null) {
            return plan.checkoutAmountMga ?? 0;
        }
        if (billing === "yearly") {
            return PricingService.yearlyTotalFromMonthly(m);
        }
        return m;
    }

    /**
     * Loads pricing plans. When `environment.useMockPricing` is true, returns mock data after a short delay.
     * Otherwise GET `${apiUrl}/website/pricing-plans` (JSON array of `PricingPlan`).
     */
    getPlans(): Observable<PricingPlan[]> {
        if (environment.useMockPricing) {
            return of([...MOCK_PRICING_PLANS]).pipe(delay(280));
        }
        const url = `${environment.apiUrl}/website/pricing-plans`;
        return this.http.get<PricingPlan[]>(url).pipe(
            catchError((err) => {
                console.warn("[PricingService] Failed to load plans", err);
                return of([]);
            }),
        );
    }

    /** Resolves a single plan by `planId` (same source as `getPlans()`). */
    getPlanById(planId: string): Observable<PricingPlan | null> {
        return this.getPlans().pipe(
            map((plans) => plans.find((p) => p.planId === planId) ?? null),
        );
    }
}
